import React from "react";
import {
  Card,
  CardHeader,
  CardBody,
  CardTitle,
  Row,
  Col,
  Badge,
  Table,
  Button,
} from "reactstrap";
import CoreEngine from "../../core/CoreEngine";
import RequestEngine from "../../core/RequestEngine";
import strings from "../../core/translate";
import { connect } from "react-redux";
import loadingAction from "../../core/redux/actions/loadingAction";

const TYPE_LABELS = {
  work_order_item_message: "Item message",
  work_order_sent: "Work order sent",
  work_order_reviewed: "Work order reviewed",
  work_order_started: "Work started",
  work_order_done: "Work done",
  booking_created: "New booking",
  booking_accepted: "Booking accepted",
  booking_rejected: "Booking rejected",
  booking_started: "Booking started",
  booking_finished: "Booking finished",
};

const TYPE_COLORS = {
  work_order_item_message: "info",
  work_order_sent: "warning",
  work_order_reviewed: "success",
  work_order_started: "primary",
  work_order_done: "dark",
  booking_created: "warning",
  booking_accepted: "success",
  booking_rejected: "danger",
  booking_started: "primary",
  booking_finished: "success",
};

const RECIPIENT_LABELS = {
  user: "Client",
  worker: "Worker",
  merchant: "Merchant",
};

class AppNotificationList extends CoreEngine {
  constructor(props) {
    super(props);
    this.state = { isLoading: true, items: [] };
    this.pathname = "app-notifications";
    this.engine = new RequestEngine();
  }

  componentDidMount() {
    this.load();
  }

  load = async () => {
    const res = await this.engine.getItem(this.pathname);
    if (res && res.status === 200) {
      this.setState({ items: res.data ?? [], isLoading: false });
    } else {
      this.setState({ isLoading: false });
    }
    this.props.loadingAction(false);
  };

  handleDelete = async (id) => {
    if (!window.confirm(strings.confirmdelete)) return;
    this.props.loadingAction(true);
    try {
      await this.engine.deleteItem(this.pathname, id);
      this.setState((prev) => ({
        items: prev.items.filter((it) => it.id !== id),
      }));
    } catch (err) {
      // a rejected delete leaves the row listed; the overlay still comes off
    }
    this.props.loadingAction(false);
  };

  handleClearAll = async () => {
    if (!window.confirm(strings.confirm_clear_notifications)) return;
    this.props.loadingAction(true);
    try {
      await this.engine.deletePath(this.pathname);
      this.setState({ items: [] });
    } catch (err) {
      // same: keep the list as it was rather than lying about an empty feed
    }
    this.props.loadingAction(false);
  };

  openWorkOrder = (workOrderId) => {
    this.props.history.push({
      pathname: `/admin/work-orders/${workOrderId}/manage`,
      state: { workOrder: { id: workOrderId } },
    });
  };

  render() {
    const { items, isLoading } = this.state;

    return (
      <div className="content">
        <Row>
          <Col md="12">
            <Card>
              <CardHeader className="d-flex justify-content-between align-items-center">
                <CardTitle tag="h5" className="mb-0">
                  {strings.notifications_feed}
                </CardTitle>
                <div className="d-flex align-items-center">
                  <Button color="default" size="sm" onClick={this.load}>
                    {strings.refresh}
                  </Button>
                  {items.length > 0 && (
                    <Button
                      color="danger"
                      size="sm"
                      className="ml-2"
                      title={strings.clear_notifications}
                      onClick={this.handleClearAll}
                    >
                      <i className="fa fa-trash mr-1" />
                      {strings.clear_all}
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardBody>
                <Table responsive striped hover>
                  <thead>
                    <tr>
                      <th>Type</th>
                      <th>To</th>
                      <th>Title</th>
                      <th>Message</th>
                      <th>Status</th>
                      <th>When</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {isLoading && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          Loading...
                        </td>
                      </tr>
                    )}
                    {!isLoading && items.length === 0 && (
                      <tr>
                        <td colSpan={7} className="text-center text-muted py-4">
                          No notifications yet.
                        </td>
                      </tr>
                    )}
                    {items.map((n) => {
                      const workOrderId = n.data && n.data.workOrderId;
                      return (
                        <tr key={n.id}>
                          <td>
                            <Badge color={TYPE_COLORS[n.type] ?? "secondary"}>
                              {TYPE_LABELS[n.type] ?? n.type}
                            </Badge>
                          </td>
                          <td>{RECIPIENT_LABELS[n.recipientType] ?? n.recipientType}</td>
                          <td>{n.title}</td>
                          <td>{n.body}</td>
                          <td>
                            {n.read ? (
                              <Badge className="notif-status notif-status-read">
                                Read
                              </Badge>
                            ) : (
                              <Badge className="notif-status notif-status-unread">
                                Unread
                              </Badge>
                            )}
                          </td>
                          <td>
                            {n.createdAt
                              ? new Date(n.createdAt).toLocaleString()
                              : "—"}
                          </td>
                          <td>
                            <div className="d-flex align-items-center">
                              {workOrderId && (
                                <Button
                                  size="sm"
                                  color="info"
                                  className="mr-2"
                                  onClick={() => this.openWorkOrder(workOrderId)}
                                >
                                  Open work order
                                </Button>
                              )}
                              <Button
                                size="sm"
                                color="danger"
                                outline
                                title={strings.delete_notification}
                                aria-label={strings.delete_notification}
                                onClick={() => this.handleDelete(n.id)}
                              >
                                <i className="fa fa-trash" />
                              </Button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </Table>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </div>
    );
  }
}

const mapDispatchToProps = (dispatch) => ({
  loadingAction: (payload) => dispatch(loadingAction(payload)),
});

export default connect(null, mapDispatchToProps)(AppNotificationList);
